import { useState } from 'react';

import { useAuth } from '@/auth/AuthContext';
import { useOrgs, useCreateOrg } from '@/hooks/useOrgs';
import { useCreateUser } from '@/hooks/useUsers';
import { ApiError } from '@/api/client';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { relTime, shortId } from '@/lib/format';
import { isSystemAdmin } from '@/types';

const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Action failed');

export function Organizations() {
  const { user: me } = useAuth();
  const orgs = useOrgs();
  const createOrg = useCreateOrg();
  const createUser = useCreateUser();

  const [orgName, setOrgName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // company-admin form
  const [caEmail, setCaEmail] = useState('');
  const [caPw, setCaPw] = useState('');
  const [caName, setCaName] = useState('');
  const [caOrg, setCaOrg] = useState('');

  if (!isSystemAdmin(me)) {
    return <EmptyState title="System admins only" hint="Only system administrators manage organizations." />;
  }

  async function addOrg() {
    setErr(null); setNote(null);
    if (!orgName.trim()) return;
    try {
      const o = await createOrg.mutateAsync(orgName.trim());
      setOrgName('');
      setNote(`Created organization “${o.name}”.`);
    } catch (e) { setErr(msgOf(e)); }
  }

  async function addCompanyAdmin() {
    setErr(null); setNote(null);
    if (!caEmail || caPw.length < 8 || !caOrg) {
      return setErr('Email, an org, and an 8+ char password are required.');
    }
    try {
      await createUser.mutateAsync({
        email: caEmail, password: caPw, display_name: caName || null,
        role: 'org_admin', org_id: caOrg,
      });
      setCaEmail(''); setCaPw(''); setCaName('');
      setNote('Company admin created. They can now sign in and add their own members.');
    } catch (e) { setErr(msgOf(e)); }
  }

  return (
    <div className="fade-up" style={{ maxWidth: 900 }}>
      <div className="page-head">
        <div className="eyebrow">administration</div>
        <div className="title">Organizations</div>
        <div className="sub">Companies (tenants). Create an org, then provision its first company admin.</div>
      </div>

      {err && <div style={{ marginBottom: 14 }} onClick={() => setErr(null)}><ErrorBanner error={err} /></div>}
      {note && <div className="card" style={{ marginBottom: 14, borderLeft: '3px solid var(--signal)' }}><span className="mono" style={{ fontSize: 13 }}>{note}</span></div>}

      <div className="row wrap" style={{ gap: 18, alignItems: 'flex-start' }}>
        <div className="card grow" style={{ minWidth: 320, display: 'grid', gap: 12 }}>
          <div className="eyebrow">new organization</div>
          <div className="field">
            <label className="label">Company name</label>
            <input className="input" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Acme Networks" />
          </div>
          <div><button className="btn btn-primary" onClick={addOrg} disabled={createOrg.isPending || !orgName.trim()}>{createOrg.isPending ? <span className="spinner" /> : 'Create org'}</button></div>
        </div>

        <div className="card grow" style={{ minWidth: 320, display: 'grid', gap: 12 }}>
          <div className="eyebrow">new company admin</div>
          <div className="field">
            <label className="label">Organization</label>
            <select className="select" value={caOrg} onChange={(e) => setCaOrg(e.target.value)}>
              <option value="">— select org —</option>
              {(orgs.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Email</label>
            <input className="input" value={caEmail} onChange={(e) => setCaEmail(e.target.value)} placeholder="admin@acme.com" />
          </div>
          <div className="field">
            <label className="label">Display name (optional)</label>
            <input className="input" value={caName} onChange={(e) => setCaName(e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Temporary password (min 8)</label>
            <input className="input" type="password" value={caPw} onChange={(e) => setCaPw(e.target.value)} />
          </div>
          <div><button className="btn btn-primary" onClick={addCompanyAdmin} disabled={createUser.isPending}>{createUser.isPending ? <span className="spinner" /> : 'Create company admin'}</button></div>
        </div>
      </div>

      <div className="eyebrow" style={{ margin: '22px 0 10px' }}>organizations ({orgs.data?.length ?? 0})</div>
      {orgs.isLoading ? (
        <Loading label="Loading orgs" />
      ) : orgs.error ? (
        <ErrorBanner error={orgs.error} />
      ) : !orgs.data || orgs.data.length === 0 ? (
        <EmptyState title="No organizations yet" hint="Create one above." />
      ) : (
        <div className="panel">
          <table className="table">
            <thead><tr><th>Name</th><th>ID</th><th>Created</th></tr></thead>
            <tbody>
              {orgs.data.map((o) => (
                <tr key={o.id}>
                  <td className="mono">{o.name}</td>
                  <td className="mono dim">{shortId(o.id)}</td>
                  <td className="dim">{relTime(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
